import { DateOnly } from '@if/core/date'
import { EPaymentFrequency } from '@if/core/types'
import { useFormContainerContext } from '@if/mui'
import { Grid, Stack } from '@mui/material'
import { BaseWorksheetFormConfig } from '@src/components/WorksheetForm'
import { computeTotalCoverageFromFunding } from '@src/services/WorksheetComputer'
import { useMemo } from 'react'
import { Control } from 'react-hook-form'
import { CalculatorValues } from '../calculatorSchema'
import { CalculatorComputeResult } from '../hooks/useCalculatorCompute'
import CalculatorParametersFields from './CalculatorParametersFields'
import CalculatorPaymentDisplay from './CalculatorPaymentDisplay'
import CalculatorResultSummary from './CalculatorResultSummary'

interface CalculatorFieldsProps {
  control: Control<CalculatorValues>
  config: BaseWorksheetFormConfig
  result: CalculatorComputeResult | null
  isComputing: boolean
  interestRate: number
  expiresOn: Date | string
  isEstimate?: boolean
  isStandalone?: boolean
}

const CalculatorFields = ({
  control,
  config,
  result,
  isComputing,
  interestRate,
  expiresOn,
  isEstimate = false,
  isStandalone = false,
}: CalculatorFieldsProps) => {
  const expiresOnDate = useMemo(() => new DateOnly(expiresOn), [expiresOn])

  const { isValid } = useFormContainerContext<CalculatorValues>()
  const hasError = !isValid

  const submittedValues = result?.values ?? null
  const computedInfo = result?.info ?? null

  const totalCoverage = computeTotalCoverageFromFunding(computedInfo ?? undefined)
  const totalObligation = result
    ? Number(result.values.amountRequested) +
      (computedInfo?.totalInterestAmount ?? 0) +
      (computedInfo?.lenderFee ?? 0) +
      totalCoverage
    : 0

  const monthlyEquivalent = useMemo(() => {
    if (!result || !computedInfo || hasError) return null
    const amount = computedInfo.paymentForFrequency ?? 0
    switch (result.values.paymentFrequency) {
      case EPaymentFrequency.Weekly:
        return amount * (52 / 12)
      case EPaymentFrequency.BiWeekly:
        return amount * (26 / 12)
      default:
        return amount
    }
  }, [result, computedInfo, hasError])

  return (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <CalculatorParametersFields
            control={control}
            config={config}
            activationMaxDate={expiresOnDate}
            isStandalone={isStandalone}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <CalculatorPaymentDisplay
            isComputing={isComputing}
            monthlyEquivalent={monthlyEquivalent}
            showProtectionPlanNote={!!result && !!submittedValues?.includeProtection}
            isEstimate={isEstimate}
          />
        </Grid>
      </Grid>

      <CalculatorResultSummary
        isComputing={isComputing}
        computedInfo={computedInfo}
        submittedValues={submittedValues}
        interestRate={interestRate}
        totalCoverage={totalCoverage}
        totalObligation={totalObligation}
      />
    </Stack>
  )
}

export default CalculatorFields
